import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../app/hooks';
import { motion } from 'framer-motion';
import { RootState } from '../app/store';
import { signInWithGoogle } from '../app/slices/authSlice';
import LoadingSpinner from '../components/LoadingSpinner';

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isLoading, error } = useSelector((state: RootState) => state.auth);
  const [urlError, setUrlError] = useState<string | null>(null);

  useEffect(() => {
    // Check for error parameter in URL
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');

    if (errorParam === 'token_expired') {
      setUrlError(
        'Your session expired due to a corrupted authentication token. Please sign in again.'
      );
      // Clear the error from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (errorParam === 'permissions') {
      setUrlError('Authentication error detected. Please sign in again.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleGoogleSignIn = async () => {
    // Clear URL error when user tries to sign in
    setUrlError(null);

    try {
      await dispatch(signInWithGoogle()).unwrap();
      // Navigation will be handled by the PublicRoute component
      // after the auth state is properly updated
    } catch (error) {
      console.error('Sign-in failed:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl w-full space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center">
            <div className="mx-auto h-20 w-20 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center shadow-2xl">
              <span className="text-white font-bold text-3xl">A</span>
            </div>
            <h2 className="mt-6 text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
              Welcome to AutoTaxPilot™
            </h2>
            <p className="mt-3 text-lg md:text-xl text-gray-700 font-semibold">
              Built for Uber/Lyft Drivers with Sole Proprietorships
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Self-Employed Rideshare Contractors • GST/HST & Expense Tracker • Canadian Tax
              Compliance
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {/* Sign In Card */}
          <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border-2 border-blue-100">
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 text-center">
                  Sign in to your account
                </h3>
                <p className="mt-2 text-base text-gray-600 text-center">
                  Track expenses, calculate GST/HST, and generate CRA reports
                </p>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-danger-50 border border-danger-200 rounded-md p-4"
                >
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-danger-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-danger-800">Sign-in Error</h3>
                      <div className="mt-2 text-sm text-danger-700">
                        <p>{error}</p>
                        {error.includes('contact support') && (
                          <p className="mt-2">
                            Support:{' '}
                            <a href="mailto:ignacioariasdev@gmail.com" className="underline">
                              ignacioariasdev@gmail.com
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {(error || urlError) && (
                <div className="rounded-md bg-red-50 border border-red-200 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">{urlError || error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <button
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="currentColor"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      Continue with Google
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-3">
                By signing in, you agree to our{' '}
                <a
                  href="/terms.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-blue-600 hover:text-blue-800"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="/privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-blue-600 hover:text-blue-800"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </div>

          {/* Marketing Features */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-2xl p-8 text-white"
          >
            <h4 className="text-2xl font-bold mb-6">Why Uber Drivers Choose Us</h4>
            <ul className="space-y-4 text-base">
              <li className="flex items-start">
                <div className="h-6 w-6 bg-emerald-400 rounded-full mr-3 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <span className="text-emerald-900 font-bold text-sm">✓</span>
                </div>
                <div>
                  <span className="font-bold">Better return for your time</span> — Maximize
                  deductions effortlessly
                </div>
              </li>
              <li className="flex items-start">
                <div className="h-6 w-6 bg-emerald-400 rounded-full mr-3 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <span className="text-emerald-900 font-bold text-sm">✓</span>
                </div>
                <div>
                  <span className="font-bold">Best deductions</span> — Never miss a claimable
                  expense
                </div>
              </li>
              <li className="flex items-start">
                <div className="h-6 w-6 bg-emerald-400 rounded-full mr-3 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <span className="text-emerald-900 font-bold text-sm">✓</span>
                </div>
                <div>
                  <span className="font-bold">Lowest price in the industry</span> — Premium
                  features, fair pricing
                </div>
              </li>
              <li className="flex items-start">
                <div className="h-6 w-6 bg-emerald-400 rounded-full mr-3 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <span className="text-emerald-900 font-bold text-sm">✓</span>
                </div>
                <div>
                  <span className="font-bold">Fewest words, fewest clicks</span> — Simple, fast
                  interface
                </div>
              </li>
              <li className="flex items-start">
                <div className="h-6 w-6 bg-emerald-400 rounded-full mr-3 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <span className="text-emerald-900 font-bold text-sm">✓</span>
                </div>
                <div>
                  <span className="font-bold">Reliable & Accurate</span> — CRA-compliant
                  calculations
                </div>
              </li>
              <li className="flex items-start">
                <div className="h-6 w-6 bg-emerald-400 rounded-full mr-3 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <span className="text-emerald-900 font-bold text-sm">✓</span>
                </div>
                <div>
                  <span className="font-bold">On your side</span> — Built by drivers, for drivers
                </div>
              </li>
              <li className="flex items-start">
                <div className="h-6 w-6 bg-emerald-400 rounded-full mr-3 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <span className="text-emerald-900 font-bold text-sm">✓</span>
                </div>
                <div>
                  <span className="font-bold">Loyal & audit-ready</span> — 6+ years of organized
                  records
                </div>
              </li>
              <li className="flex items-start">
                <div className="h-6 w-6 bg-emerald-400 rounded-full mr-3 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <span className="text-emerald-900 font-bold text-sm">✓</span>
                </div>
                <div>
                  <span className="font-bold">Top security</span> — Bank-level encryption &
                  protection
                </div>
              </li>
              <li className="flex items-start">
                <div className="h-6 w-6 bg-emerald-400 rounded-full mr-3 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <span className="text-emerald-900 font-bold text-sm">✓</span>
                </div>
                <div>
                  <span className="font-bold">Your data, your control</span> — Only you have access
                  to your files
                </div>
              </li>
            </ul>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center text-xs text-gray-500"
        >
          AutoTaxPilot™ is a product of Larson Raymond Rockwell
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
